/**
 * Browser client for saved-preview Worker endpoints.
 */

import { authedFetch } from '../shared/authed-fetch.js';

const LOCALHOST_API_ORIGIN = 'http://localhost:3001';

export class PreviewApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'PreviewApiError';
    this.status = status;
    this.data = data;
  }
}

export function getPreviewApiOrigin(win = globalThis.window) {
  const explicit =
    typeof win?.__PB_PREVIEW_ENDPOINT === 'string' ? win.__PB_PREVIEW_ENDPOINT.trim() : '';
  if (explicit) return new URL(explicit).origin;

  const compose =
    typeof win?.__PB_COMPOSE_ENDPOINT === 'string' ? win.__PB_COMPOSE_ENDPOINT.trim() : '';
  if (compose) return new URL(compose).origin;

  const protocol = win?.location?.protocol;
  const origin = win?.location?.origin;
  if ((protocol === 'http:' || protocol === 'https:') && origin) return origin;
  return LOCALHOST_API_ORIGIN;
}

async function parseResponse(resp) {
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new PreviewApiError(
      data.error || resp.statusText || `HTTP ${resp.status}`,
      resp.status,
      data
    );
  }
  return data;
}

/**
 * `AbortError` matching the shape `fetch` produces, so every caller's existing
 * `err.name === 'AbortError'` check keeps working regardless of which step in
 * the save actually noticed the abort.
 */
function abortError() {
  const err = new Error('Aborted');
  err.name = 'AbortError';
  return err;
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw abortError();
}

export async function savePreview({
  slug,
  html,
  projectName,
  sourceTemplateDomain = null,
  metaTitle = null,
  metaDescription = null,
  ogImage = null,
  projectId = null,
  projectVersion = null,
  signal = null
}) {
  const origin = getPreviewApiOrigin();
  const payload = { slug, html, projectName, sourceTemplateDomain };
  // Additive (migration 006): links the preview back to its project so the
  // publish log's `target` slug can be resolved later. Old records lack both.
  if (projectId) payload.projectId = projectId;
  if (Number.isInteger(projectVersion) && projectVersion > 0) {
    payload.projectVersion = projectVersion;
  }
  if (metaTitle) payload.metaTitle = metaTitle;
  if (metaDescription) payload.metaDescription = metaDescription;
  if (ogImage?.externalUrl) {
    payload.ogImageExternalUrl = ogImage.externalUrl;
  } else if (ogImage?.blob instanceof Blob) {
    payload.ogImageBase64 = await _blobToBase64(ogImage.blob, signal);
    payload.ogImageMime = ogImage.blob.type || 'image/png';
  }
  // Checked again after the base64 read: a multi-MB OG image is exactly the
  // window in which a user gives up and hits Cancel, and the point of the whole
  // change is that the POST must not go out afterwards.
  throwIfAborted(signal);
  const resp = await authedFetch(`${origin}/api/preview/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal
  });
  return parseResponse(resp);
}

function _blobToBase64(blob, signal = null) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    if (typeof FileReader !== 'undefined') {
      const reader = new FileReader();
      const onAbort = () => {
        reader.abort();
        reject(abortError());
      };
      signal?.addEventListener('abort', onAbort, { once: true });
      const settle = (fn) => (value) => {
        signal?.removeEventListener('abort', onAbort);
        fn(value);
      };
      const done = settle(resolve);
      const fail = settle(reject);
      reader.onload = () => {
        const result = String(reader.result || '');
        // FileReader returns data:<mime>;base64,<payload> — strip the prefix
        // so the worker just gets the raw base64. Keeps payload size honest.
        const comma = result.indexOf(',');
        done(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => fail(reader.error || new Error('Could not read OG image blob'));
      reader.readAsDataURL(blob);
      return;
    }
    blob
      .arrayBuffer()
      .then((buffer) => {
        if (signal?.aborted) {
          reject(abortError());
          return;
        }
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (const byte of bytes) binary += String.fromCharCode(byte);
        resolve(btoa(binary));
      })
      .catch(reject);
  });
}

export async function checkPreviewStatus(slug, { signal = null } = {}) {
  const origin = getPreviewApiOrigin();
  const resp = await authedFetch(`${origin}/api/preview/${encodeURIComponent(slug)}`, {
    method: 'GET',
    signal
  });
  return parseResponse(resp);
}

export async function listPreviews() {
  const origin = getPreviewApiOrigin();
  const resp = await authedFetch(`${origin}/api/preview/list`, {
    method: 'GET'
  });
  return parseResponse(resp);
}

export async function deletePreview(slug) {
  const origin = getPreviewApiOrigin();
  const resp = await authedFetch(`${origin}/api/preview/${encodeURIComponent(slug)}`, {
    method: 'DELETE'
  });
  return parseResponse(resp);
}
